import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  codeSentSchema,
  meSchema,
  registerResultSchema,
  sessionSchema,
  type CodeSent,
  type Me,
  type RegisterInput,
  type RegisterResult,
  type RegisterWithGoogleInput,
  type RequestCodeInput,
  type Session,
  type VerifyCodeInput,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Session courante : l'utilisateur et son cabinet. `undefined` tant que la
 * requête tourne, erreur 401 si le jeton n'est plus valide (le client purge
 * alors le jeton et déclenche `onUnauthorized`).
 */
export function useMe() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => api.get<Me>('/auth/me', { schema: meSchema }),
    enabled: token !== null,
    staleTime: 5 * 60_000,
  });
}

/** Raccourci : l'utilisateur seul, sans le cabinet. */
export function useCurrentUser() {
  const { data, ...rest } = useMe();
  return { ...rest, data: data?.user };
}

/** Étape 1 : demande d'un code par mail. Ne dit jamais si le compte existe. */
export function useRequestCode() {
  const api = useApi();

  return useMutation({
    mutationFn: (input: RequestCodeInput) =>
      api.post<CodeSent>('/auth/code/request', input, { schema: codeSentSchema }),
  });
}

/** Étape 2 : vérification du code. Seul endroit où un jeton est délivré. */
export function useVerifyCode() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: VerifyCodeInput) =>
      api.post<Session>('/auth/code/verify', input, { schema: sessionSchema }),
    onSuccess: ({ token, user, cabinet }) => {
      api.tokens.set(token);
      // La session est déjà connue : évite un aller-retour sur /auth/me.
      queryClient.setQueryData(queryKeys.auth.me(), { user, cabinet });
    },
  });
}

/**
 * Inscription : crée le cabinet et son premier compte (admin du cabinet),
 * puis envoie le code. Aucun jeton ici, il faut passer par `useVerifyCode`.
 */
export function useRegister() {
  const api = useApi();

  return useMutation({
    mutationFn: (input: RegisterInput) =>
      api.post<RegisterResult>('/auth/register', input, { schema: registerResultSchema }),
  });
}

/**
 * Termine une inscription entamée par Google. Ouvre la session directement :
 * Google a déjà prouvé l'adresse, il n'y a pas de code à saisir.
 */
export function useRegisterWithGoogle() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RegisterWithGoogleInput) =>
      api.post<Session>('/auth/register/google', input, { schema: sessionSchema }),
    onSuccess: ({ token, user, cabinet }) => {
      api.tokens.set(token);
      queryClient.setQueryData(queryKeys.auth.me(), { user, cabinet });
    },
  });
}

/** Déconnexion. Le jeton est sans état : l'essentiel est de le jeter ici. */
export function useLogout() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout');
      } finally {
        api.tokens.clear();
      }
    },
    onSettled: () => queryClient.clear(),
  });
}
